import { Chart as ChartJS } from "chart.js";
import { BASE_SIZE_MIN_VALUE } from "../../constants";
import { sampleSizeSymbolCheck, wrapChartCharacters } from "../../commonLogics";

export const getUsesPerDayChartOptions = (usePerDayGraph) => {
  const datasets = usePerDayGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets?.flatMap((dataset) => dataset.data), 0) + 2;
  return {
    aspectRatio: 3,
    barThickness: datasets?.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: false,
        position: "top",
        align: "start",
        labels: {
          padding: 20,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        // color: "#000",
        // anchor: "end",
        // align: "top",
        // font: { weight: "bold", size: 12 },
        // formatter: (value) => (value !== 0 ? value : ""),
        labels: {
          value: {
            align: "center",
            anchor: "center",
            color: "#fff",
            font: { weight: "bold", size: 14 },
            formatter: (value) =>
              value !== 0 ? `${parseFloat(value).toFixed(1)}` : "",
          },
          name: {
            align: "end",
            anchor: "end",
            color: "#000",
            offset: 20,
            font: { weight: "bold", size: 14 },
            formatter: (value, context) =>
              context.dataIndex !== 0 && value !== 0 ? `Index:` : "",
          },
          index: {
            align: "end",
            anchor: "end",
            color: "#000",
            font: { weight: "bold", size: 16 },
            formatter: (value, context) => {
              return context.dataIndex !== 0 &&
                value !== 0 &&
                context.dataset?.index &&
                context.dataset?.index.length
                ? context.dataset?.index[context.dataIndex]
                : "";
            },
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: { size: 12, weight: "bold" },
          color: (context) => {
            const Index = context.index;
            const baseSizeValue = datasets[Index]?.participant[Index];
            return baseSizeValue < BASE_SIZE_MIN_VALUE
              ? "#6c757d" // previous color #dc3545(red)
              : "#6C757D";
          },
          callback: function (value, index, values) {
            const label = datasets[index]?.label;
            // const labelVal =
            //   label.length > 18 ? label.slice(0, 18) + "..." : label;
            // return `${labelVal} (N=${datasets[index]?.participant[index]})`; // Custom label based on the index
            return [
              ...wrapChartCharacters(label),
              `(n=${datasets[index]?.participant[index]} products)${sampleSizeSymbolCheck(datasets[index]?.participant[index], "products")}`,
            ];
          },
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        ticks: {
          callback: (value) => `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const getWeeklyChartOptions = (usePerWeekGraph) => {
  const datasets = usePerWeekGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets?.flatMap((dataset) => dataset.data), 0) + 1;

  return {
    aspectRatio: 3,
    barThickness: datasets?.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: false,
        position: "top",
        align: "start",
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        // color: "#000",
        // anchor: "end",
        // align: "top",
        // font: { weight: "bold", size: 12 },
        // formatter: (value) => (value !== 0 ? value : ""),
        labels: {
          value: {
            align: "center",
            anchor: "center",
            color: "#fff",
            font: { weight: "bold", size: 14 },
            formatter: (value) =>
              value !== 0 ? `${parseFloat(value).toFixed(1)}` : "",
          },
          name: {
            align: "end",
            anchor: "end",
            color: "#000",
            offset: 20,
            font: { weight: "bold", size: 14 },
            formatter: (value, context) =>
              context.dataIndex !== 0 && value !== 0 ? `Index:` : "",
          },
          index: {
            align: "end",
            anchor: "end",
            color: "#000",
            font: { weight: "bold", size: 16 },
            formatter: (value, context) => {
              return context.dataIndex !== 0 &&
                value !== 0 &&
                context.dataset?.index &&
                context.dataset?.index.length
                ? context.dataset?.index[context.dataIndex]
                : "";
            },
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: { size: 12, weight: "bold" },
          color: (context) => {
            const Index = context.index;
            const baseSizeValue = datasets[Index]?.participant[Index];
            return baseSizeValue < BASE_SIZE_MIN_VALUE
              ? "#6c757d" // previous color #dc3545(red)
              : "#6C757D";
          },
          callback: function (value, index, values) {
            const label = datasets[index]?.label;
            // const labelVal =
            //   label.length > 18 ? label.slice(0, 18) + "..." : label;
            // return `${labelVal} (N=${datasets[index]?.participant[index]})`; // Custom label based on the index
            return [
              ...wrapChartCharacters(label),
              `(n=${datasets[index]?.participant[index]} products)${sampleSizeSymbolCheck(datasets[index]?.participant[index], "products")}`,
            ];
          },
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        ticks: {
          callback: (value) => `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const getTimeOfDayChartOptions = (timeOfDayGraph, yLabel) => {
  const labels = timeOfDayGraph?.labels || [];
  const datasets = timeOfDayGraph?.datasets || [];
  return {
    barThickness: timeOfDayGraph?.labels?.length === 1 ? 300 : undefined,
    aspectRatio: 3,
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
        // reverse: true,
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            const datasetsVal = chart.data.datasets;
            labels.forEach((dataset, i) => {
              const baseSizeNVal = datasetsVal[i]?.total_response
                ? datasetsVal[i]?.total_response
                : datasetsVal[i]?.participant?.reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0,
                  );
              // Add N value text to each label
              dataset.text = `${dataset.text} (n=${baseSizeNVal} uses)`;
              dataset.fontColor =
                baseSizeNVal < BASE_SIZE_MIN_VALUE
                  ? "#6c757d" // previous color #dc3545(red)
                  : "#6C757D";
            });

            return labels;
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: false,
      // datalabels: {
      //   color: "#fff",
      //   anchor: "center",
      //   align: "center",
      //   font: { weight: "bold", size: 12 },
      //   formatter: (value) => (value !== 0 ? `${value}%` : ""),
      // },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: yLabel,
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          callback: (value) =>
            yLabel === "% OF TOTAL" ? `${value}%` : `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
      x: {
        ticks: {
          font: { size: 12, weight: "bold" },
        },
      },
    },
    // scales: {
    //   x: {
    //     stacked: true,
    //     ticks: {
    //       font: { size: 12, weight: "bold" },
    //       callback: function (value, index, values) {
    //         const labels = timeOfDayGraph?.labels || [];
    //         const label = labels[index];
    //         const labelVal =
    //           label.length > 18 ? label.slice(0, 18) + "..." : label;
    //         return `${labelVal}`; // Custom label based on the index
    //       },
    //     },
    //   },
    //   y: {
    //     stacked: true,
    //     ticks: {
    //       font: { size: 12, weight: "bold" },
    //     },
    //   },
    // },
  };
};

export const getDayOfWeekChartOptions = (dayOfWeekGraph, yLabel) => {
  const datasets = dayOfWeekGraph?.datasets || [];
  const labels = dayOfWeekGraph?.labels || [];
  const maxValue =
    Math.max(...datasets?.flatMap((dataset) => dataset.data), 0) + 1;

  return {
    aspectRatio: 3,
    barThickness: dayOfWeekGraph?.labels?.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
        // reverse: yLabel === "% OF TOTAL" ? true : false,
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            const datasetsVal = chart.data.datasets;
            labels.forEach((dataset, i) => {
              const baseSizeNVal = datasetsVal[i]?.participant?.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0,
              );
              // Add N value text to each label
              dataset.text = `${dataset.text} (n=${baseSizeNVal} uses)`;
              dataset.fontColor =
                baseSizeNVal < BASE_SIZE_MIN_VALUE
                  ? "#6c757d" // previous color #dc3545(red)
                  : "#6C757D";
            });

            return labels;
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        color: "#fff",
        anchor: "center",
        align: "center",
        font: { weight: "bold", size: 12 },
        formatter: (value) =>
          value !== 0
            ? yLabel === "% OF TOTAL"
              ? `${value}%`
              : `${value}`
            : "",
      },
    },
    scales: {
      x: {
        // stacked: yLabel === "% OF TOTAL" ? true : false,
        ticks: {
          font: { size: 12, weight: "bold" },
          // color: (context) => {
          //   const Index = context.index;
          //   let baseSizeNVal = 0;
          //   datasets.forEach((dataset) => {
          //     baseSizeNVal += dataset?.participant
          //       ? dataset?.participant[Index]
          //       : 0;
          //   });
          //   return baseSizeNVal < BASE_SIZE_MIN_VALUE
          // ? "#6c757d" // previous color #dc3545(red)
          // : "#6C757D";
          // },
          // callback: function (value, index, values) {
          //   const label = labels[index];
          //   const labelVal =
          //     label.length > 18 ? label.slice(0, 18) + "..." : label;
          //   if (yLabel === "% OF TOTAL") {
          //     let baseSizeNVal = 0;
          //     datasets.forEach((dataset) => {
          //       baseSizeNVal += dataset?.participant
          //         ? dataset?.participant[index]
          //         : 0;
          //     });
          //     return `${labelVal} (N=${baseSizeNVal})`; // Custom label based on the index
          //   } else {
          //     return `${labelVal}`; // Custom label based on the index
          //   }
          // },
        },
      },
      y: {
        // stacked: yLabel === "% OF TOTAL" ? true : false,
        beginAtZero: true,
        suggestedMax: maxValue,
        title: {
          display: true,
          text: yLabel,
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          stepSize: 25,
          callback: (value) =>
            yLabel === "% OF TOTAL"
              ? `${value}%`
              : `${parseFloat(value).toFixed(1)}`,
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const getWeekPartGraphOptions = (weekPartGraph, yLabel) => {
  const datasets = weekPartGraph?.datasets || [];
  const labels = weekPartGraph?.labels || [];
  const maxValue =
    Math.max(...datasets?.flatMap((dataset) => dataset.data), 0) + 1;

  return {
    aspectRatio: 3,
    barThickness: weekPartGraph?.labels?.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: true,
        position: yLabel === "% OF TOTAL" ? "right" : "top",
        align: "start",
        reverse: yLabel === "% OF TOTAL" ? true : false,
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            if (yLabel === "% OF TOTAL") {
              return labels;
            } else {
              const datasetsVal = chart.data.datasets;
              labels.forEach((dataset, i) => {
                const baseSizeNVal = datasetsVal[i]?.participant?.reduce(
                  (accumulator, currentValue) => accumulator + currentValue,
                  0,
                );
                // Add N value text to each label
                dataset.text = `${dataset.text} (n=${baseSizeNVal} uses)`;
                dataset.fontColor =
                  baseSizeNVal < BASE_SIZE_MIN_VALUE
                    ? "#6C757D" // previous color #dc3545(red)
                    : "#6C757D";
              });

              return labels;
            }
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      annotation: {
        annotations: {
          targetLine: {
            type: "line",
            scaleID: "y",
            value: 29, // ← y-axis value where the line appears
            borderColor: "#6C757D",
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              display: true,
              content: "29%",
              color: "#fff",
              position: "start",
            },
          },
        },
      },
      datalabels: {
        color: "#fff",
        anchor: "center",
        align: "center",
        // offset: yLabel === "% OF TOTAL" ? 20 : 0,
        font: { weight: "bold", size: 12 },
        formatter: (value) =>
          value !== 0
            ? yLabel === "% OF TOTAL"
              ? `${value}%`
              : `${parseFloat(value).toFixed(1)}`
            : "",
      },
    },
    scales: {
      y: {
        stacked: yLabel === "% OF TOTAL" ? true : false,
        beginAtZero: true,
        suggestedMax: maxValue,
        title: {
          display: true,
          text: yLabel,
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          callback: (value) =>
            yLabel === "% OF TOTAL" ? `${value}%` : `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
      x: {
        stacked: yLabel === "% OF TOTAL" ? true : false,
        ticks: {
          font: { size: 12, weight: "bold" },
          color: (context) => {
            if (yLabel === "% OF TOTAL") {
              const Index = context.index;
              let baseSizeNVal = 0;
              datasets.forEach((dataset) => {
                baseSizeNVal += dataset?.participant
                  ? dataset?.participant[Index]
                  : 0;
              });
              return baseSizeNVal < BASE_SIZE_MIN_VALUE
                ? "#6c757d" // previous color #dc3545(red)
                : "#6C757D";
            } else {
              return "#6C757D";
            }
          },
          callback: function (value, index, values) {
            const label = labels[index];
            // const labelVal =
            //   label.length > 18 ? label.slice(0, 18) + "..." : label;
            if (yLabel === "% OF TOTAL") {
              let baseSizeNVal = 0;
              datasets.forEach((dataset) => {
                if (dataset?.total_participant) {
                  baseSizeNVal = dataset?.total_participant[index]
                    ? dataset?.total_participant[index]
                    : 0;
                } else {
                  baseSizeNVal += dataset?.participant
                    ? dataset?.participant[index]
                      ? dataset?.participant[index]
                      : 0
                    : 0;
                }
              });
              return [
                ...wrapChartCharacters(label),
                `(n=${baseSizeNVal} uses)`,
              ];
            } else {
              return `${label}`; // Custom label based on the index
            }
          },
        },
      },
    },
  };
};

export const getHmlChartOptions = (hmlGraph) => {
  const datasets = hmlGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets?.flatMap((dataset) => dataset.data), 0) + 2;
  return {
    aspectRatio: 3,
    barThickness: datasets?.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        align: "start",
        labels: {
          padding: 20,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
        },
      },
      datalabels: {
        // color: "#fff",
        // anchor: "center",
        // align: "center",
        // font: { weight: "bold", size: 12 },
        labels: {
          value: {
            align: "center",
            anchor: "center",
            color: "#fff",
            font: { weight: "bold", size: 12 },
            formatter: (value) =>
              value !== 0 ? `${parseFloat(value).toFixed(1)}` : "",
          },
          name: {
            align: "start",
            anchor: "start",
            color: "#6C757D",
            font: { weight: "bold", size: 12 },
            rotation: -13,
            formatter: (value, context) => {
              const dataIndex = context.dataIndex;
              return value !== 0
                ? [
                    `(n=${context?.dataset?.participant[dataIndex]} products)${sampleSizeSymbolCheck(context?.dataset?.participant[dataIndex], "products")}`,
                  ]
                : "";
            },
          },
        },
      },
      tooltip: {
        enabled: false,
        callbacks: {
          label: function (context) {
            const dataIndex = context.dataIndex;
            // Custom tooltip label
            return [
              `${context.dataset.label}: ${context.raw}`,
              `(n=${context?.dataset?.participant[dataIndex]} products)${sampleSizeSymbolCheck(context?.dataset?.participant[dataIndex], "products")}`,
            ];
          },
          title: function (tooltipItems) {
            // Custom tooltip title
            return `${tooltipItems[0].label}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: { size: 12, weight: "bold" },
          callback: function () {
            return "";
          },
        },
      },
      x2: {
        position: "top",
        ticks: {
          font: { size: 14, weight: "bold" },
          color: "#495057",
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        ticks: {
          stepSize: 2,
          callback: (value) => `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};
